import React, { useState } from 'react';
import { MapPinIcon, GlobeIcon } from 'lucide-react';
import { format } from 'date-fns';
import { useWorkspace } from '../store/workspaceStore';
import { PropertyStatus, LISTING_PLATFORMS } from '../types';
import { Topbar } from '../components/layout/Topbar';
import { Avatar } from '../components/common/Avatar';
const STATUS_META: Record<PropertyStatus, {
  label: string;
  cls: string;
}> = {
  active: {
    label: 'Active',
    cls: 'bg-emerald-100 text-emerald-700'
  },
  under_contract: {
    label: 'Under Contract',
    cls: 'bg-amber-100 text-amber-700'
  },
  sold: {
    label: 'Sold',
    cls: 'bg-slate-200 text-slate-600'
  }
};
const TABS: {
  key: 'all' | PropertyStatus;
  label: string;
}[] = [{
  key: 'all',
  label: 'All'
}, {
  key: 'active',
  label: 'Active'
}, {
  key: 'under_contract',
  label: 'Under Contract'
}, {
  key: 'sold',
  label: 'Sold'
}];
export function PropertiesBoard() {
  const properties = useWorkspace(s => s.properties);
  const personById = useWorkspace(s => s.personById);
  const togglePlatform = useWorkspace(s => s.togglePropertyPlatform);
  const setStatus = useWorkspace(s => s.setPropertyStatus);
  const [tab, setTab] = useState<'all' | PropertyStatus>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const list = tab === 'all' ? properties : properties.filter(p => p.status === tab);
  return <div className="flex min-h-full flex-1 flex-col" data-id="element-266" data-mp="2ydescgx1gre">
      <Topbar title="Active / Sold Properties" subtitle="Every listing and exactly where it's posted." data-id="element-267" data-mp="2x221n5hsyo5" />

      <div className="mp-scroll flex-1 overflow-y-auto p-6" data-id="element-268" data-mp="3u56d98kbr1s8">
        <div className="mb-4 flex flex-wrap gap-1.5" data-id="element-269" data-mp="3pdej6o1ymdxc">
          {TABS.map(t => <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${tab === t.key ? 'bg-brand text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`} data-id="element-270" data-mp="3kf4oeek5dfc9">
              {t.label}
            </button>)}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-271" data-mp="2b8cll84af73p">
          {list.map(p => {
          const broker = personById(p.brokerId);
          const meta = STATUS_META[p.status];
          const open = openId === p.id;
          return <div key={p.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-card" data-id="element-272" data-mp="26dfdq4iqv3se">
                <div className="flex items-start justify-between gap-2" data-id="element-273" data-mp="1rkc6or2s48fx">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${meta.cls}`} data-id="element-274" data-mp="1winekw9ik8vi">{meta.label}</span>
                  <select value={p.status} onChange={e => setStatus(p.id, e.target.value as PropertyStatus)} className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-500 outline-none" aria-label="Change status" data-id="element-275" data-mp="1ije144cozsv8">
                    <option value="active" data-id="element-276" data-mp="3ry3w4zbnnw7v">Active</option>
                    <option value="under_contract" data-id="element-277" data-mp="3rylq1pauhycm">Under Contract</option>
                    <option value="sold" data-id="element-278" data-mp="3n0k857p5o52q">Sold</option>
                  </select>
                </div>

                <h3 className="mt-3 text-base font-bold text-slate-900" data-id="element-279" data-mp="4m2wlnhnjpd4">{p.name}</h3>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400" data-id="element-280" data-mp="3vyw840bwwf7u">
                  <MapPinIcon className="h-3.5 w-3.5" data-id="element-281" data-mp="eixsdymjjbnp" /> {p.address}
                </p>

                <div className="mt-3 flex items-center justify-between" data-id="element-282" data-mp="9kmixp05r360">
                  <div data-id="element-283" data-mp="9mqmg5u5a05r">
                    <p className="text-lg font-bold text-slate-900" data-id="element-284" data-mp="njvsi5tuyz10">{p.price}</p>
                    <p className="text-xs text-slate-400" data-id="element-285" data-mp="jik8qgxo180q">{p.assetType}</p>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-286" data-mp="jgpiqo7jepps">
                    <Avatar person={broker} size={30} data-id="element-287" data-mp="2pc0p3umayki2" />
                    <span className="text-xs text-slate-500" data-id="element-288" data-mp="2la6ua2ysgapt">{broker?.name}</span>
                  </div>
                </div>

                <div className="mt-4 border-t border-slate-100 pt-3" data-id="element-289" data-mp="2mq13lcljepeq">
                  <button onClick={() => setOpenId(open ? null : p.id)} className="flex w-full items-center justify-between text-xs font-semibold text-slate-600" data-id="element-290" data-mp="2rpx95g9vuowe">
                    <span className="inline-flex items-center gap-1.5" data-id="element-291" data-mp="30pslvpj36ryj">
                      <GlobeIcon className="h-4 w-4 text-cyan-500" data-id="element-292" data-mp="240urw9j5uy2f" /> Listed on {p.platforms.length} platform{p.platforms.length !== 1 ? 's' : ''}
                    </span>
                    <span className="text-brand-accent" data-id="element-293" data-mp="2803gkzzcg5r4">{open ? 'Hide' : 'Manage'}</span>
                  </button>

                  {!open && <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-294" data-mp="2hs9dmo4gddd6">
                      {p.platforms.map(plat => <span key={plat} className="rounded-full bg-cyan-50 px-2 py-0.5 text-[11px] font-medium text-cyan-700" data-id="element-295" data-mp="1kbtzucgzm86u">{plat}</span>)}
                      {p.platforms.length === 0 && <span className="text-[11px] text-slate-400" data-id="element-296" data-mp="1hquqp892c0a9">Not listed yet</span>}
                    </div>}

                  {open && <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-297" data-mp="17tq457bb22sx">
                      {LISTING_PLATFORMS.map(plat => {
                  const active = p.platforms.includes(plat);
                  return <button key={plat} onClick={() => togglePlatform(p.id, plat)} className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${active ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`} data-id="element-298" data-mp="yucykpqaxydh">
                            {plat}
                          </button>;
                })}
                    </div>}
                  {p.listedAt && <p className="mt-2 text-[11px] text-slate-400" data-id="element-299" data-mp="1i7p79yh6ge0">Listed {format(new Date(p.listedAt), 'MMM d, yyyy')}</p>}
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
}